import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AttendeesTab, HomeworkTab, AssessmentsTab } from './admin/lectures/LectureTabs';

const API = process.env.REACT_APP_BACKEND_URL;
const SLUG = 'sound-of-authority';
const toSec = (s) => {
  const v = String(s || '').trim();
  if (!v) return null;
  const p = v.split(':').map(Number);
  if (p.some((n) => Number.isNaN(n) || n < 0)) return NaN;
  return p.reduce((a, n) => a * 60 + n, 0);
};
const toTxt = (n) => (n == null ? '' : `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`);

const ClipRow = ({ v, token, onSaved }) => {
  const [start, setStart] = useState(toTxt(v.start || 0));
  const [end, setEnd] = useState(toTxt(v.end));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const s = toSec(start) ?? 0, e = toSec(end);
    if (Number.isNaN(s) || Number.isNaN(e) || (e != null && e <= s)) { toast.error('Tempi non validi: usa m:ss e fine dopo l’inizio'); return; }
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/admin/lectures/${SLUG}/clips/${v.id}`, { start: s, end: e }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(r.data); toast.success(`Salvato: ${v.label}`);
    } catch { toast.error('Salvataggio non riuscito'); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-[140px_1fr_110px_110px_auto] gap-3 items-center rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-clip-${v.id}`}>
      <img src={`https://i.ytimg.com/vi/${v.youtube}/mqdefault.jpg`} alt="" className="w-full rounded-lg" />
      <div>
        <p className="font-bold text-slate-900">{v.label}</p>
        <p className="text-sm text-slate-600">{v.title}</p>
        <p className="text-xs text-slate-400">{v.channel}</p>
      </div>
      <label className="text-xs text-slate-500">Inizio (m:ss)
        <input value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" data-testid={`admin-clip-start-${v.id}`} />
      </label>
      <label className="text-xs text-slate-500">Fine (vuoto = fino alla fine)
        <input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="es. 0:45" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" data-testid={`admin-clip-end-${v.id}`} />
      </label>
      <div className="flex gap-2">
        <a href={`https://www.youtube.com/watch?v=${v.youtube}&t=${toSec(start) || 0}s`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:text-blue-700" title="Prova su YouTube" data-testid={`admin-clip-preview-${v.id}`}><ExternalLink className="w-4 h-4" /></a>
        <button onClick={save} disabled={busy} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50" data-testid={`admin-clip-save-${v.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
        </button>
      </div>
    </div>
  );
};

export default function AdminLecturesPage() {
  const { token, isAdmin, loading } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('attendees');
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/admin/lectures/${SLUG}/clips`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setData(r.data)).catch(() => setData({ videos: [] }));
  }, [token]);
  if (!loading && !isAdmin()) return <Navigate to="/login?next=/admin/lectures" replace />;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-lectures-page">
      <div className="max-w-5xl mx-auto px-5 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Lectures · CRM</p>
            <h1 className="text-3xl font-extrabold text-slate-900">{data?.title || 'The Sound of Authority'}</h1>
            <p className="text-sm text-slate-500">Iscritti, compiti da valutare, richieste di valutazione e tempi delle clip.</p>
          </div>
          <Link to={`/${SLUG}`} className="text-sm font-semibold text-blue-700 underline" data-testid="admin-lectures-open-page">Apri la pagina</Link>
        </div>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-blue-600" /> : (
          <>
            <div className="flex flex-wrap gap-1 rounded-xl bg-white border border-slate-200 p-1 w-fit" data-testid="admin-lectures-tabs">
              {[['attendees', 'Iscritti'], ['homework', 'Compiti'], ['assessments', 'Valutazioni'], ['clips', 'Clip video']].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`admin-lectures-tab-${k}`} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === k ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>{l}</button>
              ))}
            </div>
            {tab === 'attendees' && <AttendeesTab slug={SLUG} token={token} />}
            {tab === 'homework' && <HomeworkTab slug={SLUG} token={token} />}
            {tab === 'assessments' && <AssessmentsTab slug={SLUG} token={token} />}
            {tab === 'clips' && data.videos.map((v) => <ClipRow key={v.id} v={v} token={token} onSaved={() => {}} />)}
          </>
        )}
      </div>
    </div>
  );
}
